import { Button, Field, Icon, TextField } from '@emotive/ui-core';
import {
  faLock,
  faUser,
  faArrowLeft,
} from '@fortawesome/pro-regular-svg-icons';
import { faExclamationSquare } from '@fortawesome/pro-solid-svg-icons';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import Recaptcha from 'react-google-recaptcha';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import tw from 'twin.macro';

import Alert from '../../components/Alert';
import { Header, TextBlock } from '../../components/text';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { useQueryString } from '../../hooks/useQueryString';
import useTemplateContext from '../../hooks/useTemplateContext';
import { BASE_API_URL } from '../../services';
import { originsMatch } from '../../utils';
import { FormCTAButton, FormSeparator } from './form-primitives';

const LoginForm = ({ submitting, error, handleLogin = () => {} }) => {
  const recaptchaRef = useRef();
  const [shopifyLogin, setShopifyLogin] = useState(false);

  useDocumentTitle('Emotive Login');

  const values = useQueryString();
  const {
    recaptcha_enabled: isRecaptchaEnabled,
    recaptcha_site_key: recaptchaSiteKey,
  } = useTemplateContext();
  const { register, handleSubmit, errors, reset } = useForm({
    mode: 'onSubmit',
  });

  const { next, referrer } = values;
  const isOAuthError = Boolean(values.error && values.error_description);

  const isShopifyEmbedded = originsMatch(
    referrer,
    process.env.SHOPIFY_EMBEDDED_HOST,
  );

  useEffect(() => reset({ email: null, password: null }), [reset]);

  const handleGoogleAuthButtonClick = (e) => {
    e.preventDefault();
    location.href =
      `${BASE_API_URL}/auth/v2/google/auth` + (next ? `?next=${next}` : '');
  };

  const onSubmit = async (data) => {
    const { username, password, shopify } = data;
    try {
      const recaptchaToken = isRecaptchaEnabled
        ? await recaptchaRef?.current?.executeAsync()
        : undefined;
      handleLogin({
        recaptchaToken,
        username,
        password,
        shopify,
        next,
        isShopifyEmbedded,
        shopifyLogin,
      });
    } catch (error) {
      console.error('login error: ', error);
    } finally {
      recaptchaRef?.current?.reset();
    }
  };

  useEffect(() => {
    window.localStorage.setItem('$$USER_LOGIN$$', 'multi');
  }, []);

  return (
    <div tw="relative flex flex-col min-w-0 px-4 pb-6">
      {recaptchaSiteKey && (
        <Recaptcha
          ref={recaptchaRef}
          size="invisible"
          sitekey={recaptchaSiteKey}
        />
      )}
      <div tw="flex flex-col gap-4">
        <Header tw="text-2xl">Log in to your account</Header>
        <div tw="flex flex-wrap gap-x-1" css={[!shopifyLogin && tw`mb-4`]}>
          <TextBlock size="small">Don&apos;t have an account yet?</TextBlock>
          <TextBlock size="small">
            <a
              tw="text-blueberry-500 hover:text-blueberry-700"
              href="mailto:support@emotive.io"
              target="_blank"
              rel="noreferrer"
            >
              Contact Us
            </a>
          </TextBlock>
        </div>
        {!isShopifyEmbedded && !shopifyLogin && (
          <>
            <FormCTAButton
              type="google"
              onClick={handleGoogleAuthButtonClick}
            />
            <FormCTAButton
              type="shopify"
              onClick={() => setShopifyLogin(true)}
            />
            <FormSeparator tw="my-1">or</FormSeparator>
          </>
        )}
        {shopifyLogin && (
          <Button
            type="button"
            variant="tertiary-link"
            data-testid="login-form-shopify-back-button-link"
            tw="inline-flex px-0 font-normal"
            onClick={() => setShopifyLogin(false)}
          >
            <Icon icon={faArrowLeft} tw="mr-2" /> Back
          </Button>
        )}
        {/* @todo: separate default login form & shopify login form */}
        <form
          tw="flex flex-col text-center gap-4"
          onSubmit={handleSubmit(onSubmit)}
          data-testid="login-form"
        >
          {shopifyLogin ? (
            <Field validationState={errors.shopify ? 'error' : 'unknown'}>
              <TextField.Input
                data-testid="login-form-shopify-input"
                id="shopify"
                name="shopify"
                ref={register({ required: true })}
                placeholder="Shopify domain"
                suffix=".myshopify.com"
              />
              {errors.shopify && (
                <Field.Help>A Shopify domain is required</Field.Help>
              )}
            </Field>
          ) : (
            <React.Fragment>
              {error && (
                <Alert severity="error">
                  <div tw="text-base text-center leading-5">
                    <span tw="inline-block pr-2 translate-y-0.5">
                      <Icon tw="text-error-dark" icon={faExclamationSquare} />
                    </span>
                    Please enter a correct email or username and password. Note
                    that both fields may be case-sensitive.
                  </div>
                </Alert>
              )}
              {isOAuthError && (
                <Alert severity="error">
                  <div tw="text-base text-center leading-5">
                    <span tw="inline-block pr-2 translate-y-0.5">
                      <Icon tw="text-error-dark" icon={faExclamationSquare} />
                    </span>
                    User is not allowed
                  </div>
                </Alert>
              )}
              <Field validationState={errors.username ? 'error' : 'unknown'}>
                <TextField.Input
                  autoFocus
                  data-testid="login-form-username-input"
                  icon={faUser}
                  id="username"
                  ref={register({ required: true })}
                  name="username"
                  placeholder="Email or Username"
                />
                {errors.username && (
                  <Field.Help>Email or Username is required</Field.Help>
                )}
              </Field>
              <Field validationState={errors.password ? 'error' : 'unknown'}>
                <TextField.Input
                  data-testid="login-form-password-input"
                  icon={faLock}
                  id="password"
                  ref={register({ required: true })}
                  name="password"
                  placeholder="Password"
                  type="password"
                />
                {errors.password && (
                  <Field.Help>Password is required</Field.Help>
                )}
              </Field>
            </React.Fragment>
          )}
          <Button
            tw="mt-4"
            loading={submitting}
            type="submit"
            data-testid="login-form-submit-button"
          >
            Login
          </Button>
          {!shopifyLogin && (
            <div tw="flex flex-col">
              <Button
                variant="tertiary-link"
                as={Link}
                data-testid="login-form-forgot-password-link"
                to="/login/reset"
                tw="font-normal"
              >
                Forgot password?
              </Button>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

LoginForm.propTypes = {
  submitting: PropTypes.bool,
  error: PropTypes.bool,
  allowRememberUser: PropTypes.bool,
  handleLogin: PropTypes.func,
  children: PropTypes.node,
};

export default LoginForm;
